import React, { useRef, useState } from 'react';
import { FiX, FiFile, FiCheck, FiAlertCircle } from 'react-icons/fi';
import ProgressBar from 'react-bootstrap/ProgressBar';
import ImageView from 'Components/Image/ImageView';
import Styles from './SupportingDocumentsUpload.module.scss';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.rtf'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/rtf',
  'text/rtf',
];

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const SupportingDocumentsUpload = ({
  files,
  setFiles,
  extractedContent,
  isExtracting,
  extractionProgress,
  errors,
  setErrors,
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const validateFile = (file) => {
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    if (
      !ALLOWED_EXTENSIONS.includes(extension) &&
      !ALLOWED_MIME_TYPES.includes(file.type)
    ) {
      return `${file.name}: Invalid file type. Allowed: PDF, DOCX, TXT, RTF`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name}: File too large (max 10MB)`;
    }
    return null;
  };

  const handleFiles = (newFiles) => {
    const fileArray = Array.from(newFiles);
    const currentCount = files.length;

    if (currentCount + fileArray.length > MAX_FILES) {
      setErrors([
        `Maximum ${MAX_FILES} files allowed. You have ${currentCount} files.`,
      ]);
      return;
    }

    const validFiles = [];
    const newErrors = [];

    fileArray.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
      } else if (!files.some((f) => f.name === file.name)) {
        validFiles.push(file);
      }
    });

    setErrors(newErrors);
    if (validFiles.length > 0) {
      setFiles([...files, ...validFiles]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    // Reset input value so same file can be selected again after removal
    e.target.value = '';
  };

  return (
    <div className={Styles.container}>
      <label className={Styles.label}>
        Supporting Documents{' '}
        <span className={Styles.optional}>(Optional)</span>
      </label>
      <p className={Styles.hint}>
        Upload reference materials to help AI generate more customized content
      </p>

      {/* Drag & Drop Area */}
      <div
        className={`${Styles.dropZone} ${dragActive ? Styles.dropZoneActive : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <ImageView src="/Images/uploadIcon.svg" class="img-fluid" />
        <p className={Styles.dropText}>
          <span className={Styles.highlight}>Click to upload</span> or drag and
          drop
        </p>
        <p className={Styles.formatText}>
          PDF, TXT, DOCX or RTF (max. 10MB each, up to {MAX_FILES} files)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.rtf"
          onChange={handleInputChange}
          className={Styles.hiddenInput}
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className={Styles.fileList}>
          {files.map((file, index) => (
            <div key={index} className={Styles.fileItem}>
              <div className={Styles.fileIcon}>
                <FiFile size={20} />
              </div>
              <div className={Styles.fileInfo}>
                <span className={Styles.fileName}>{file.name}</span>
                <span className={Styles.fileSize}>{formatBytes(file.size)}</span>
              </div>
              {extractedContent && !isExtracting && (
                <FiCheck className={Styles.successIcon} />
              )}
              <FiX
                className={Styles.removeIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {isExtracting && (
        <div className={Styles.progressContainer}>
          <ProgressBar
            now={extractionProgress}
            className={Styles.progressBar}
          />
          <span className={Styles.progressText}>
            Extracting content... {extractionProgress}%
          </span>
        </div>
      )}

      {/* Errors */}
      {errors && errors.length > 0 && (
        <div className={Styles.errors}>
          {errors.map((error, i) => (
            <div key={i} className={Styles.errorItem}>
              <FiAlertCircle size={14} /> {error}
            </div>
          ))}
        </div>
      )}

      {/* Success indicator */}
      {extractedContent && !isExtracting && (
        <div className={Styles.successMessage}>
          <FiCheck size={14} /> Content extracted from {files.length} file(s)
        </div>
      )}
    </div>
  );
};

export default SupportingDocumentsUpload;
